import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PreflightPanel } from "./PreflightPanel";
import { EnrichQueuePanel } from "./EnrichQueuePanel";
import { FocusPanel } from "./FocusPanel";

/**
 * What runs without a human, grouped by what it does to the pipeline — and the
 * place to switch it on or off.
 *
 * Includes automations that do NOT exist yet, on purpose. A row reading
 * "social distribution — manual, no scheduled job" is information; leaving it out
 * makes the gap look handled, which is how one survives for months. Those rows
 * carry no switch, because there is nothing behind it to turn on.
 *
 * Numbers are saved on blur rather than per keystroke: typing "30000" through a
 * per-keystroke save would briefly persist 3, then 30, then 300 — each a value
 * the scheduler could read if it fired mid-edit.
 */

// "manual" ≠ "not-built". Manual means built and button-fireable but never
// scheduled; not-built means the code path does not exist. See the route.
type AutomationState = "on" | "off" | "manual" | "not-built";

interface Setting {
  key: string;
  label: string;
  value: string;
  hint: string;
}

interface Schedule {
  days: number[];
  times: string[];
  timezone: string;
  cron: string;
}

interface AutomationRow {
  id: string;
  group: "production" | "data" | "discovery" | "maintenance" | "distribution";
  name: string;
  what: string;
  envKey?: string;
  settings?: Setting[];
  schedule?: Schedule;
  state: AutomationState;
  cron?: string | null;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  note?: string | null;
  detailLines?: string[];
  inFlight?: boolean;
  totalRuns?: number;
  runnable?: boolean;
  runEndpoint?: string;
  runActions?: { label: string; body?: Record<string, unknown> }[];
}

interface ArticleOutcome {
  keyword: string;
  status: "produced" | "published" | "failed" | "skipped";
  steps: { step: string; ok: boolean; detail: string; skipped?: boolean }[];
  seoScore: number | null;
  error: string | null;
  publishHeldBecause: string | null;
}

interface AutopilotRun {
  status: "running" | "done" | "error" | "blocked";
  trigger: "cron" | "manual";
  startedAt: string;
  finishedAt: string | null;
  blockedReason: string | null;
  publishing: boolean;
  articles: ArticleOutcome[];
  skipped: { keyword: string; reason: string; detail?: string }[];
  spentUsd: number;
  currentKeyword: string | null;
  currentStep: string | null;
}

const GROUPS: { id: AutomationRow["group"]; title: string; blurb: string }[] = [
  { id: "production", title: "Content production", blurb: "Writing and publishing articles." },
  { id: "data", title: "Data syncs", blurb: "Keeping the mirrors honest. Everything downstream trusts these." },
  { id: "discovery", title: "Discovery", blurb: "Finding new things worth writing about." },
  { id: "maintenance", title: "Maintenance", blurb: "The rollback path for every automated change." },
  { id: "distribution", title: "Distribution", blurb: "Turning a published article into everything else." },
];

function fmt(ts?: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  if (mins < 60) return diff >= 0 ? `${mins}m ago` : `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return diff >= 0 ? `${hrs}h ago` : `in ${hrs}h`;
  return d.toLocaleDateString();
}

function StatePill({ state, inFlight }: { state: AutomationState; inFlight?: boolean }) {
  if (inFlight) {
    return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">running…</span>;
  }
  const map: Record<AutomationState, string> = {
    on: "bg-emerald-100 text-emerald-700",
    off: "bg-slate-100 text-slate-600",
    manual: "bg-sky-100 text-sky-700",
    "not-built": "bg-amber-100 text-amber-700",
  };
  const label: Record<AutomationState, string> = {
    on: "On",
    off: "Off",
    manual: "Manual",
    "not-built": "Not built",
  };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${map[state]}`}>{label[state]}</span>;
}

function Toggle({
  on,
  disabled,
  onChange,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
        on ? "bg-emerald-500" : "bg-slate-300"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          on ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

/** Number field that only commits on blur or Enter. See the file header. */
function NumberSetting({
  setting,
  disabled,
  onCommit,
}: {
  setting: Setting;
  disabled?: boolean;
  onCommit: (key: string, value: string) => void;
}) {
  const [draft, setDraft] = useState(setting.value);
  const dirty = draft !== setting.value;

  const commit = () => {
    const trimmed = draft.trim();
    // A blank or non-positive limit would read as "unlimited" to some callers
    // and "zero" to others — refuse it and snap back rather than guess.
    if (!trimmed || !Number.isFinite(Number(trimmed)) || Number(trimmed) <= 0) {
      setDraft(setting.value);
      return;
    }
    if (trimmed !== setting.value) onCommit(setting.key, trimmed);
  };

  return (
    <label className="flex items-start gap-2">
      <input
        type="number"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setDraft(setting.value);
        }}
        className={`w-24 rounded border px-2 py-1 text-xs tabular-nums disabled:bg-slate-50 disabled:text-slate-400 ${
          dirty ? "border-amber-400 bg-amber-50" : "border-slate-300"
        }`}
      />
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-medium text-slate-700">{setting.label}</span>
        <span className="block text-[11px] leading-snug text-slate-500">{setting.hint}</span>
      </span>
    </label>
  );
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
/** Israel's working week. The single most likely answer, so it gets one click. */
const WEEKDAYS_IL = [0, 1, 2, 3, 4];

/**
 * Days + times for a scheduled job.
 *
 * The schedule used to be reachable only as `AUTOPILOT_CRON` in an env var,
 * which makes "only on weekdays" a cron-syntax question instead of a business
 * one. Days are buttons and times are a list; the compiled cron stays visible
 * underneath so the mapping is never a mystery.
 *
 * TIMES ARE THE SCHEDULER'S ZONE, shown explicitly next to the fields. The
 * container runs in UTC, so before this the shipped "06:00" default fired at
 * 09:00 in Jerusalem — a picker that repeated that lie would be worse than the
 * cron string it replaces.
 */
function ScheduleEditor({
  schedule,
  disabled,
  onSave,
}: {
  schedule: Schedule;
  disabled?: boolean;
  onSave: (next: { days: number[]; times: string[] }) => void;
}) {
  const [days, setDays] = useState<number[]>(schedule.days);
  const [times, setTimes] = useState<string[]>(schedule.times);

  // Re-sync when the server's view changes (another tab, or our own save landing).
  const serverKey = `${schedule.days.join(",")}|${schedule.times.join(",")}`;
  const [seenKey, setSeenKey] = useState(serverKey);
  if (serverKey !== seenKey) {
    setSeenKey(serverKey);
    setDays(schedule.days);
    setTimes(schedule.times);
  }

  const dirty = days.join(",") !== schedule.days.join(",") || times.join(",") !== schedule.times.join(",");
  const valid = days.length > 0 && times.length > 0;

  const toggleDay = (d: number) =>
    setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b)));

  const setTime = (i: number, v: string) => setTimes((cur) => cur.map((t, j) => (j === i ? v : t)));
  const addTime = () => setTimes((cur) => [...cur, "06:00"]);
  const removeTime = (i: number) => setTimes((cur) => cur.filter((_, j) => j !== i));

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-800">Schedule</h4>
        <span className="text-[11px] text-slate-500">
          All times are <span className="font-medium text-slate-700">{schedule.timezone}</span>
        </span>
      </div>

      <div className="mt-3">
        <span className="block text-xs font-medium text-slate-700">Days</span>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {DAY_NAMES.map((label, d) => {
            const on = days.includes(d);
            return (
              <button
                key={label}
                type="button"
                disabled={disabled}
                onClick={() => toggleDay(d)}
                aria-pressed={on}
                className={`h-8 w-12 rounded border text-xs font-medium transition-colors disabled:opacity-40 ${
                  on
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            );
          })}
          <button
            type="button"
            disabled={disabled}
            onClick={() => setDays(WEEKDAYS_IL)}
            className="ml-1 h-8 rounded border border-slate-300 px-2 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            title="Sunday–Thursday, the Israeli working week"
          >
            Weekdays
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setDays([0, 1, 2, 3, 4, 5, 6])}
            className="h-8 rounded border border-slate-300 px-2 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Every day
          </button>
        </div>
      </div>

      <div className="mt-3">
        <span className="block text-xs font-medium text-slate-700">Times</span>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {times.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1">
              <input
                type="time"
                value={t}
                disabled={disabled}
                onChange={(e) => setTime(i, e.target.value)}
                className="rounded border border-slate-300 px-2 py-1 text-xs tabular-nums disabled:bg-slate-50"
              />
              {times.length > 1 && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeTime(i)}
                  className="text-xs text-slate-400 hover:text-red-600 disabled:opacity-40"
                  aria-label="Remove this time"
                >
                  ✕
                </button>
              )}
            </span>
          ))}
          <button
            type="button"
            disabled={disabled || times.length >= 6}
            onClick={addTime}
            className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            + Add time
          </button>
        </div>
        <p className="mt-1 text-[11px] leading-snug text-slate-500">
          Each time is a full run — the per-run article limit applies to each one, not to the day.
        </p>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
        <button
          type="button"
          disabled={disabled || !dirty || !valid}
          onClick={() => onSave({ days, times })}
          className="rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-40"
        >
          Save schedule
        </button>
        {!valid && <span className="text-[11px] text-red-600">Pick at least one day and one time.</span>}
        {dirty && valid && <span className="text-[11px] text-amber-700">Unsaved changes</span>}
        <code className="ml-auto text-[11px] text-slate-400">{schedule.cron}</code>
      </div>
    </div>
  );
}

/**
 * The last (or in-flight) autopilot run, shown inline. A run takes minutes and
 * spends money, so "what did it just do and what did it refuse to do" has to be
 * visible without digging through server logs.
 */
function RunPanel({ run, running }: { run: AutopilotRun | null; running: boolean }) {
  if (!run) return null;
  const tone =
    run.status === "error" || run.status === "blocked"
      ? "border-amber-300 bg-amber-50"
      : running
        ? "border-blue-300 bg-blue-50"
        : "border-slate-200 bg-slate-50";

  return (
    <div className={`mt-3 rounded border p-3 text-xs ${tone}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-800">
          {running ? "Run in progress" : `Last run (${run.trigger})`}
        </span>
        <span className="text-slate-500">{new Date(run.startedAt).toLocaleString()}</span>
        <span className="text-slate-500">· ${run.spentUsd.toFixed(2)} spent</span>
        {!run.publishing && <span className="text-slate-500">· holding for review</span>}
      </div>

      {running && run.currentKeyword && (
        <div className="mt-1 text-blue-800">
          {run.currentStep ?? "working"} — “{run.currentKeyword}”
        </div>
      )}

      {run.blockedReason && <div className="mt-2 text-amber-800">{run.blockedReason}</div>}

      {run.articles.map((a, i) => (
        <div key={i} className="mt-2 border-t border-slate-200 pt-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{a.keyword}</span>
            <span
              className={
                a.status === "published"
                  ? "text-emerald-700"
                  : a.status === "failed"
                    ? "text-red-700"
                    : "text-slate-600"
              }
            >
              {a.status}
            </span>
            {a.seoScore !== null && <span className="text-slate-500">SEO {a.seoScore}/100</span>}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {a.steps.map((st, j) => (
              <span
                key={j}
                title={st.detail}
                className={`rounded px-1.5 py-0.5 text-[11px] ${
                  !st.ok
                    ? "bg-red-100 text-red-700"
                    : st.skipped
                      ? "bg-slate-100 text-slate-500"
                      : "bg-emerald-100 text-emerald-700"
                }`}
              >
                {st.step}
              </span>
            ))}
          </div>
          {a.error && <div className="mt-1 text-red-700">{a.error}</div>}
          {a.publishHeldBecause && <div className="mt-1 text-slate-600">{a.publishHeldBecause}</div>}
        </div>
      ))}

      {run.skipped.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-slate-600">
            {run.skipped.length} keyword(s) skipped — every one has a reason
          </summary>
          <ul className="mt-1 space-y-0.5 text-slate-500">
            {run.skipped.slice(0, 20).map((sk, i) => (
              <li key={i}>
                {sk.keyword} — {sk.reason}
                {sk.detail ? ` (${sk.detail})` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export function AutomationsPage() {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<{ rows: AutomationRow[]; counts: Record<AutomationState, number> }>({
    queryKey: ["automations"],
    queryFn: async () => {
      const r = await fetch("/api/automations", { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    // A row that runs in the background (the inbound-link sweep) reports its
    // outcome through this payload, so poll fast while any row is in flight.
    refetchInterval: (q) => (q.state.data?.rows.some((r) => r.inFlight) ? 3_000 : 30_000),
  });

  const { data: runData } = useQuery<{ running: boolean; run: AutopilotRun | null }>({
    queryKey: ["autopilot-run"],
    queryFn: async () => {
      const r = await fetch("/api/automations/autopilot/run", { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    // Poll fast while a run is live (steps take tens of seconds), slowly when idle.
    refetchInterval: (q) => (q.state.data?.running ? 3_000 : 30_000),
  });

  const runNow = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/automations/autopilot/run", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ force: true }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
      return body;
    },
    onMutate: () => setErr(null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["autopilot-run"] });
      void qc.invalidateQueries({ queryKey: ["automations"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  /**
   * Run-now for every row that is NOT the autopilot.
   *
   * The autopilot keeps its own button because it has a pre-flight and a live
   * step-by-step panel to show. Everything else just needs "fire it, tell me
   * what happened" — and needs to post to ITS OWN endpoint, which is why the row
   * carries `runEndpoint`. Before this, a second runnable row would have started
   * an article-generation run.
   */
  const [rowRunNote, setRowRunNote] = useState<{ id: string; text: string } | null>(null);
  const runRow = useMutation({
    mutationFn: async (v: { id: string; endpoint: string; body?: Record<string, unknown> }) => {
      const r = await fetch(v.endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(v.body ?? {}),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
      return { id: v.id, body };
    },
    onMutate: () => { setErr(null); setRowRunNote(null); },
    onSuccess: ({ id, body }) => {
      const res = body?.result;
      // Say what the run DID, not that it returned 200 — a sweep that declined
      // to run and a sweep that linked nothing look identical otherwise. A run
      // that went to the background has no result yet: the row's note fills in
      // from `lastResult` once it finishes.
      const text = body?.started
        ? "Started in the background — the result appears under the row when it finishes."
        : res
        ? res.skippedReason
          ? `Skipped: ${res.skippedReason}`
          : `${res.belowTarget ?? res.orphansFound ?? 0} under target · ${res.eligible ?? 0} in window · ${res.attempted ?? 0} attempted · ${res.linksWritten ?? 0} link(s) written${res.dryRun ? " (dry run — nothing written)" : ""}`
        : "done";
      setRowRunNote({ id, text });
      void qc.invalidateQueries({ queryKey: ["automations"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const save = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, string> }) => {
      const r = await fetch(`/api/automations/${encodeURIComponent(id)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(updates),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
      return { id };
    },
    onMutate: () => setErr(null),
    onSuccess: ({ id }) => {
      setSavedId(id);
      window.setTimeout(() => setSavedId((c) => (c === id ? null : c)), 2000);
      void qc.invalidateQueries({ queryKey: ["automations"] });
      // The Credentials → Autopilot card reads the same env keys.
      void qc.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const saveSchedule = useMutation({
    mutationFn: async (next: { days: number[]; times: string[] }) => {
      const r = await fetch("/api/automations/autopilot/schedule", {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(next),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
      return body as { cron: string; nextRunAt: string | null };
    },
    onMutate: () => setErr(null),
    onSuccess: () => {
      setSavedId("autopilot");
      window.setTimeout(() => setSavedId((c) => (c === "autopilot" ? null : c)), 2000);
      void qc.invalidateQueries({ queryKey: ["automations"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  if (isLoading) return <div className="p-6 text-sm text-slate-500">Loading automations…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">Could not load automations: {String(error)}</div>;
  const rows = data?.rows ?? [];

  // The misconfiguration most likely to be read as "it's running": publishing
  // armed while the master switch is off means nothing is produced OR published.
  const master = rows.find((r) => r.id === "autopilot");
  const publish = rows.find((r) => r.id === "autopilot-publish");
  const armedButIdle = publish?.state === "on" && master?.state === "off";

  return (
    <div className="space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Automations</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          Everything that runs without you, and the switches that control it. Changes save immediately and take
          effect on the next run — no restart. <span className="font-medium">Manual</span> means the capability is
          built and works, but only when you press the button on the article — there is no schedule, so it never
          happens on its own. <span className="font-medium">Not built</span> means the code path does not exist yet.
        </p>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700">
            {data?.counts.on ?? 0} on
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
            {data?.counts.off ?? 0} off
          </span>
          <span className="rounded-full bg-sky-100 px-2 py-0.5 font-medium text-sky-700">
            {data?.counts.manual ?? 0} manual
          </span>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700">
            {data?.counts["not-built"] ?? 0} not built
          </span>
          {save.isPending && <span className="text-slate-500">saving…</span>}
        </div>
        {err && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>
        )}
        {armedButIdle && (
          <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span className="font-medium">Publishing is on, but Autopilot is off.</span> Nothing is being produced or
            published. Turn Autopilot on, or turn publishing off so the intent is unambiguous.
          </div>
        )}
      </header>

      <FocusPanel />

      {GROUPS.map((g) => {
        const items = rows.filter((r) => r.group === g.id);
        if (!items.length) return null;
        return (
          <section key={g.id}>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{g.title}</h2>
            <p className="mb-3 text-xs text-slate-500">{g.blurb}</p>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="w-14 px-4 py-2 font-medium">On</th>
                    <th className="px-4 py-2 font-medium">Automation</th>
                    <th className="px-4 py-2 font-medium">State</th>
                    <th className="px-4 py-2 font-medium">Schedule</th>
                    <th className="px-4 py-2 font-medium">Last run</th>
                    <th className="px-4 py-2 font-medium">Next</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((r) => {
                    const switchable = Boolean(r.envKey) && r.state !== "not-built" && r.state !== "manual";
                    // Scoped to THIS row: one shared mutation would otherwise
                    // put every runnable row's buttons into "Running…".
                    const rowBusy = runRow.isPending && runRow.variables?.id === r.id;
                    return (
                      <Fragment key={r.id}>
                      <tr className="align-top">
                        <td className="px-4 py-3">
                          {switchable ? (
                            <Toggle
                              on={r.state === "on"}
                              disabled={save.isPending}
                              onChange={(next) =>
                                save.mutate({ id: r.id, updates: { [r.envKey!]: next ? "on" : "off" } })
                              }
                            />
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{r.name}</span>
                            {savedId === r.id && <span className="text-[11px] text-emerald-600">saved</span>}
                          </div>
                          <div className="mt-0.5 max-w-xl text-xs text-slate-500">{r.what}</div>
                          {r.note && <div className="mt-1 text-xs text-slate-600">{r.note}</div>}
                          {r.detailLines && r.detailLines.length > 0 && (
                            <details className="mt-1 max-w-2xl text-xs text-slate-600">
                              <summary className="cursor-pointer">Last run, {r.detailLines.length} item(s)</summary>
                              <pre dir="auto" className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 font-sans">
                                {r.detailLines.join("\n\n")}
                              </pre>
                            </details>
                          )}
                          {r.envKey && <code className="mt-1 block text-[11px] text-slate-400">{r.envKey}</code>}
                          {r.runnable && r.id === "autopilot" && (
                            <button
                              type="button"
                              disabled={runNow.isPending || Boolean(runData?.running)}
                              onClick={() => runNow.mutate()}
                              className="mt-2 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                              {runData?.running ? "Running…" : "Run now"}
                            </button>
                          )}
                          {r.runnable && r.id === "autopilot" && (
                            <span className="ml-2 text-[11px] text-slate-500">
                              Fires one real run regardless of the switch.
                            </span>
                          )}
                          {/* Every other runnable row posts to its OWN endpoint.
                              A shared button that always hit the autopilot's URL
                              would start an article run from someone else's label. */}
                          {r.runnable && r.id !== "autopilot" && r.runEndpoint && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {(r.runActions ?? [{ label: "Run now" }]).map((a) => (
                                <button
                                  key={a.label}
                                  type="button"
                                  disabled={rowBusy || Boolean(r.inFlight)}
                                  onClick={() => runRow.mutate({ id: r.id, endpoint: r.runEndpoint!, ...(a.body ? { body: a.body } : {}) })}
                                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                  {rowBusy || r.inFlight ? "Running…" : a.label}
                                </button>
                              ))}
                              {rowRunNote?.id === r.id && (
                                <span className="text-[11px] text-slate-600">{rowRunNote.text}</span>
                              )}
                            </div>
                          )}
                          {/* Pre-flight sits ABOVE the last run on purpose: the
                              question before pressing the button is "what is
                              about to happen", not "what happened". */}
                          {r.runnable && r.id === "autopilot" && <EnrichQueuePanel />}
                          {r.runnable && r.id === "autopilot" && <PreflightPanel />}
                          {r.runnable && r.id === "autopilot" && <RunPanel run={runData?.run ?? null} running={Boolean(runData?.running)} />}
                        </td>
                        <td className="px-4 py-3">
                          <StatePill state={r.state} inFlight={r.inFlight} />
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          {r.cron ? <code>{r.cron}</code> : "—"}
                          {typeof r.totalRuns === "number" && (
                            <div className="mt-0.5 text-slate-400">{r.totalRuns} run(s)</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{fmt(r.lastRunAt)}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{fmt(r.nextRunAt)}</td>
                      </tr>
                      {/* Schedule sits ABOVE the limits: "when does this run" is
                          the question an operator arrives with, and it was the
                          one thing this page could not answer without editing an
                          env var. */}
                      {r.schedule ? (
                        <tr className="border-t-0">
                          <td />
                          <td colSpan={5} className="px-4 pb-3">
                            <ScheduleEditor
                              schedule={r.schedule}
                              disabled={saveSchedule.isPending}
                              onSave={(next) => saveSchedule.mutate(next)}
                            />
                          </td>
                        </tr>
                      ) : null}
                      {/* Limits get the full table width — squeezed into the name
                          column the hints wrap to one word per line. */}
                      {r.settings?.length ? (
                        <tr className="border-t-0">
                          <td />
                          <td colSpan={5} className="px-4 pb-4">
                            <div className="grid gap-x-6 gap-y-3 rounded border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-3">
                              {r.settings.map((st) => (
                                <NumberSetting
                                  key={st.key}
                                  setting={st}
                                  disabled={save.isPending}
                                  onCommit={(key, value) => save.mutate({ id: r.id, updates: { [key]: value } })}
                                />
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
